import React, { createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Nav from './components/Nav';
import Footer from './components/Footer';
import Home from './pages/Home';
import SecurityPage from './pages/SecurityPage';
import PerformancePage from './pages/PerformancePage';
import DevPlatformPage from './pages/DevPlatformPage';
import AIPage from './pages/AIPage';
import SaaSPage from './pages/SaaSPage';
import { useFeatureFlags, FeatureFlags } from './hooks/useFeatureFlags';

// Feature flags context so any component can access flags
const FeatureFlagsContext = createContext<FeatureFlags | null>(null);

export function useFlags(): FeatureFlags {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFlags must be used within FeatureFlagsProvider');
  return ctx;
}

function App() {
  const flags = useFeatureFlags();

  // Apply dark mode class to body based on flag
  React.useEffect(() => {
    if (flags.darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [flags.darkMode]);

  return (
    <FeatureFlagsContext.Provider value={flags}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className={`portal ${flags.darkMode ? 'dark' : ''}`}>
          <Nav />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/performance" element={<PerformancePage />} />
            <Route path="/devplatform" element={<DevPlatformPage />} />
            <Route path="/ai" element={<AIPage />} />
            <Route path="/saas" element={<SaaSPage />} />
          </Routes>
          <Footer />
          {/* Flag status indicator */}
          <FlagIndicator flags={flags} />
        </div>
      </BrowserRouter>
    </FeatureFlagsContext.Provider>
  );
}

function FlagIndicator({ flags }: { flags: FeatureFlags }) {
  const [expanded, setExpanded] = React.useState(false);
  const activeCount = flags.allFlags.filter(f => f.enabled).length;

  return (
    <div className="flag-indicator" data-expanded={expanded}>
      <button className="flag-indicator-toggle" onClick={() => setExpanded(!expanded)}>
        <span className="flag-icon">🚩</span>
        <span className="flag-count">{activeCount}/{flags.allFlags.length}</span>
        {flags.loading && <span className="flag-loading">⟳</span>}
      </button>
      {expanded && (
        <div className="flag-indicator-panel">
          <div className="flag-panel-header">
            <span>Feature Flags</span>
            <a href="https://l2.shruti-techlab.com" target="_blank" rel="noreferrer">
              Open FlagForge ↗
            </a>
          </div>
          {flags.error && <div className="flag-error">Error: {flags.error}</div>}
          <div className="flag-list">
            {flags.allFlags.map(f => (
              <div key={f.key} className="flag-item">
                <span className={`flag-dot ${f.enabled ? 'on' : 'off'}`} />
                <span className="flag-key">{f.key}</span>
                <span className="flag-status">{f.enabled ? 'ON' : 'OFF'}</span>
                {f.rolloutPercent < 100 && f.enabled && (
                  <span className="flag-rollout">{f.rolloutPercent}%</span>
                )}
              </div>
            ))}
            {flags.allFlags.length === 0 && !flags.loading && (
              <div className="flag-empty">No flags. Seed them in FlagForge.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
